import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCustomerKey,
  ShopifyGiftCardKey,
  ShopifyGiftCardToCustomerKey,
  ShopifyGiftCardToOrderKey,
  ShopifyGiftCardToShopKey,
  ShopifyOrderKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyGiftCardKey,
  "2022-01": {
    name: "Shopify Gift Card",
    graphQLTypename: "GiftCard",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Balance",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "balance",
            graphQLSelection: `amount currencyCode`,
          },
        },
      },
      {
        name: "Initial Value",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "initialValue", graphQLSelection: `amount currencyCode` } },
      },
      {
        name: "Last Characters",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lastCharacters" } },
      },
      {
        name: "Masked Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "maskedCode" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "note" } },
      },
      {
        name: "Expires On",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "expiresOn" } },
        configuration: {
          includeTime: false,
        },
      },
      {
        name: "Enabled",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "enabled" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt" } },
      },
      {
        name: "Disabled At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "disabledAt" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToOrderKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "order", graphQLSelection: "id" },
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToCustomerKey,
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "customer", graphQLSelection: "id" },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyGiftCardToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "giftCards",
      filters: {
        createdAt: { sortKey: "CREATED_AT" },
      },
      sortableBy: {
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        amountSpent: { sortKey: "AMOUNT_SPENT", reverse: true },
        balance: { sortKey: "BALANCE", reverse: true },
        code: { sortKey: "CODE", reverse: false },
        customerName: { sortKey: "CUSTOMER_NAME", reverse: false },
        disabledAt: { sortKey: "DISABLED_AT", reverse: true },
        expiresOn: { sortKey: "EXPIRES_ON", reverse: false },
        id: { sortKey: "ID", reverse: false },
        initialValue: { sortKey: "INITIAL_VALUE", reverse: true },
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
      },
    },
  },
  "2024-10": {
    fields: [
      {
        name: "Disabled At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "disabledAt" } },
        isDeleted: true,
      },
      {
        name: "Deactivated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "deactivatedAt" } },
      },
    ],
  },
};

export const ShopifyGiftCard = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
